import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Lock } from "lucide-react";
import { BRAND } from "../lib/brand";
import { apiFetch, setToken } from "../lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      setToken(res.token);
      nav("/admin");
    } catch (e) {
      setErr("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center text-center">
          <div className="bg-black rounded-full p-1 ring-1 ring-white/15 mb-6">
            <img src={BRAND.logo} alt="Rockstar ATVs" className="h-14 w-14 rounded-full" />
          </div>
          <span className="inline-flex items-center gap-2 border border-[color:var(--primary)]/50 text-[color:var(--primary)] px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-mono mb-5">
            <Lock size={12} /> Admin Access
          </span>
          <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.95]">
            Rockstar <span className="text-[color:var(--primary)]">Command</span>
          </h1>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            Sign in to view submitted waivers, booking leads, and download signed client profiles.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3" data-testid="admin-login-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            autoFocus
            className="w-full bg-[color:var(--bg-elev)] border border-white/15 focus:border-[color:var(--primary)] text-white px-4 py-3.5 outline-none transition-colors text-sm"
            data-testid="admin-email"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-[color:var(--bg-elev)] border border-white/15 focus:border-[color:var(--primary)] text-white px-4 py-3.5 outline-none transition-colors text-sm"
            data-testid="admin-password"
          />
          {err && (
            <p className="text-[color:var(--primary)] text-sm font-semibold text-center" data-testid="admin-login-error">
              {err}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60 text-white font-bold uppercase tracking-[0.16em] text-[11px] px-6 py-3.5 transition-colors"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign In"} <ArrowRight size={13} />
          </button>
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-white/40 justify-center w-full pt-2">
            <ShieldCheck size={12} className="text-[color:var(--primary)]" /> Secure admin session · 24 hr token
          </p>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-[10px] uppercase tracking-[0.22em] font-mono text-white/50 hover:text-white transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> Back to Site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
